import { useEffect, useState } from "react";

type Props = {
  /** Aantal pagina's in de PDF, al bepaald door caller via pdfjs. 0 = nog onbekend. */
  pageCount: number;
  /** True zodra server klaar is; stopt timer en toont final state. */
  done?: boolean;
};

/**
 * Pseudo-progress UI tijdens /api/upload. Backend streamt geen events,
 * dus we schatten op basis van tijd:
 * - ~15s per pagina OCR (TurboOCR hires + pdf_mode=ocr)
 * - ~10s Gemini-call voor velden-interpretatie
 * - 1-2s nabewerking
 *
 * Memory: Wim wilde dat klant tijdens de ~40s eerste-upload-latency ziet
 * "pagina 1, pagina 2, AI-analyse" i.p.v. een statische spinner. Dit is
 * pseudo (tijd-based); upgrade naar echte SSE-progress is optional later.
 */
export function UploadProgress({ pageCount, done }: Props) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (done) return;
    const start = performance.now();
    const t = setInterval(() => {
      setElapsed(Math.round((performance.now() - start) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [done]);

  const perPage = 15; // seconden per pagina OCR-schatting
  const ocrTotal = Math.max(1, pageCount) * perPage;
  const geminiStart = ocrTotal;
  const geminiEnd = geminiStart + 12;

  const stage = computeStage(elapsed, pageCount, geminiStart, geminiEnd, perPage);

  return (
    <div className="border rounded p-8 text-center bg-white">
      <div className="animate-pulse text-gray-700 font-medium">{stage.label}</div>
      <div className="mt-3 w-full max-w-md mx-auto bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className="bg-blue-500 h-2 transition-all duration-500"
          style={{ width: `${stage.percent}%` }}
        />
      </div>
      <div className="text-xs text-gray-400 mt-3">
        {pageCount > 0 ? (
          <>
            {pageCount} pagina{pageCount === 1 ? "" : "'s"} · {elapsed}s verstreken
          </>
        ) : (
          <>Document voorbereiden… · {elapsed}s</>
        )}
      </div>
      <div className="text-[11px] text-gray-400 mt-1">
        Eerste upload van een nieuwe layout: ~{Math.round(ocrTotal + 12)}s. Volgende uploads
        van hetzelfde type gaan onder de 100 ms.
      </div>
    </div>
  );
}

function computeStage(
  elapsed: number,
  pageCount: number,
  geminiStart: number,
  geminiEnd: number,
  perPage: number
): { label: string; percent: number } {
  if (pageCount <= 0) {
    return { label: "Document voorbereiden…", percent: 5 };
  }
  if (elapsed < geminiStart) {
    const pageIdx = Math.min(pageCount, Math.floor(elapsed / perPage) + 1);
    const inPage = elapsed - (pageIdx - 1) * perPage;
    const label = `Pagina ${pageIdx} van ${pageCount} wordt gelezen (OCR)…`;
    const pctPage = (pageIdx - 1 + inPage / perPage) / pageCount;
    return { label, percent: Math.min(70, pctPage * 70) };
  }
  if (elapsed < geminiEnd) {
    const g = (elapsed - geminiStart) / (geminiEnd - geminiStart);
    return {
      label: "AI analyseert de inhoud (velden + tabel)…",
      percent: 70 + g * 25,
    };
  }
  return { label: "Resultaten verwerken…", percent: 97 };
}
